import { z } from 'zod';
import { digits, optionalText } from './common';
import { zip5 } from './configuration';

/**
 * Lifeline enrollment (the owner's request of 2026-10-28). The wizard: 1 Personal Info,
 * 2 Consent, 3 Upload docs, Audio Auth, National Verifier, 4 Enroll; then the order finishes with
 * the prepaid handover and payment steps. Telgoo5's tenant sells no Lifeline, so the layout is
 * the owner's screenshot; the consent wording, the documents and the verifier rules are 212Ez's.
 * The National Verifier is checked through the Lifeline (NLAD) connection, simulated on a Trial company.
 */

/** "Eligibility Program": the federal programs that qualify a household for Lifeline. */
export const ELIGIBILITY_PROGRAMS = [
  'Medicaid',
  'Supplemental Nutrition Assistance Program (SNAP)',
  'Supplemental Security Income (SSI)',
  'Federal Public Housing Assistance (FPHA)',
  "Veterans Pension and Survivors' Benefit",
  'Bureau of Indian Affairs General Assistance',
  'Tribally-Administered TANF',
  'Food Distribution Program on Indian Reservations (FDPIR)',
  'Tribal Head Start (income-qualifying)',
  'Household income at or below 135% of the Federal Poverty Guidelines',
  // Owner, 2026-11-04, as on zipbellwireless.com. Lifeline's federal rules do not list it (it qualified
  // for ACP); offered while the switch `lifeline-program-details` is on.
  'School Lunch/Breakfast',
] as const;
export type EligibilityProgram = (typeof ELIGIBILITY_PROGRAMS)[number];

export const FPHA_PROGRAM = 'Federal Public Housing Assistance (FPHA)';
export const SCHOOL_PROGRAM = 'School Lunch/Breakfast';
/** "Public Housing Type", asked when the program is Federal Public Housing Assistance. */
export const PUBLIC_HOUSING_TYPES = [
  'Housing Choice Voucher Program',
  'Project-based rental assistance',
  'Public housing',
  'Affordable Housing Programs for American Indians, Alaska Natives or Native Hawaiians',
] as const;

/** "Who receives the benefit?" */
export const LIFELINE_BENEFICIARIES = ['Myself', 'Child or Dependent in household'] as const;

/** Where an application is in the Lifeline steps before the plan is chosen. */
export const LIFELINE_STAGES = ['consent', 'documents', 'audio', 'verifier', 'enroll'] as const;
export type LifelineStage = (typeof LIFELINE_STAGES)[number];

export const VERIFIER_STATUSES = ['Approved', 'Pending', 'Rejected'] as const;
export type VerifierStatus = (typeof VERIFIER_STATUSES)[number];

export const LIFELINE_DOCUMENT_KINDS = [
  'Proof of eligibility',
  'Proof of identity',
  'Proof of address',
  'Other',
] as const;
export const LIFELINE_DOCUMENT_TYPES = ['application/pdf', 'image/jpeg', 'image/png'] as const;
export const LIFELINE_DOCUMENT_MAX_BYTES = 5 * 1024 * 1024;

export const AUDIO_AUTH_METHODS = ['Recorded call', 'Not needed'] as const;

/** Why a line stops being Lifeline ("Remove Lifeline" on the customer page). */
export const LIFELINE_REMOVAL_REASONS = [
  'Failed recertification',
  'No longer eligible',
  'Another Lifeline benefit in the household',
  'Customer request',
  'De-enrolled by the National Verifier',
] as const;

/**
 * Step 2, Consent: what the applicant agrees to, in 212Ez's own words. Every statement must be
 * ticked, and the applicant types their full name as a signature.
 */
export const LIFELINE_CONSENTS = [
  'I (or my dependent) take part in the program I chose, or my household income is at or below 135% of the Federal Poverty Guidelines.',
  'No one else in my household receives a Lifeline benefit, and I will not get more than one.',
  'I will recertify my eligibility every year and will tell the company within 30 days if I stop qualifying.',
  'I allow my information to be shared with the National Verifier and the National Lifeline Accountability Database to check my eligibility.',
  'The information I gave is true. Giving false information to get Lifeline can be punished by law.',
] as const;

const required = (label: string, max: number) =>
  z
    .string({ required_error: `${label} is required` })
    .trim()
    .min(1, `${label} is required`)
    .max(max);

const thisYear = new Date().getUTCFullYear();
const dateParts = {
  dobMonth: z.coerce
    .number({ invalid_type_error: 'Select Month' })
    .int()
    .min(1, 'Select Month')
    .max(12, 'Select Month'),
  dobDay: z.coerce.number({ invalid_type_error: 'Select Day' }).int().min(1, 'Select Day').max(31),
  dobYear: z.coerce
    .number({ invalid_type_error: 'Select Year' })
    .int()
    .min(thisYear - 120, 'Select Year')
    .max(thisYear, 'Select Year'),
};

/** The date of birth as YYYY-MM-DD, or null when the day does not exist (31 February). */
export function dobOf(month: number, day: number, year: number): string | null {
  const d = new Date(Date.UTC(year, month - 1, day));
  return d.getUTCMonth() === month - 1 && d.getUTCDate() === day
    ? d.toISOString().slice(0, 10)
    : null;
}

const ssnLast4 = z.preprocess(
  (v) => (typeof v === 'string' && v.trim() === '' ? undefined : v),
  digits(4, 4, 'SSN (Last 4 Digits)').optional(),
);
const tribalId = optionalText(20);

/**
 * Step 1, Personal Info. SSN (Last 4 Digits) OR Tribal ID; for a child or dependent, their name
 * and date of birth too. The State follows the ZIP code. Fields that depend on each other are
 * optional in the object and checked in `superRefine`, so one answer names every missing field.
 */
export const lifelinePersonalSchema = z
  .object({
    firstName: required('First Name', 40),
    lastName: required('Last Name', 40),
    email: z.preprocess(
      (v) => (typeof v === 'string' && v.trim() === '' ? undefined : v),
      z.string().trim().email('Enter a valid Email').max(100).optional(),
    ),
    primaryPhone: digits(10, 10, 'Primary Phone'),
    ...dateParts,
    ssnLast4,
    tribalId,
    eligibilityProgram: z
      .string({ required_error: 'Select Eligibility Program' })
      .refine((v) => (ELIGIBILITY_PROGRAMS as readonly string[]).includes(v), {
        message: 'Select Eligibility Program',
      }),
    beneficiary: z.enum(LIFELINE_BENEFICIARIES).default('Myself'),
    publicHousingType: optionalText(100),
    schoolName: optionalText(100),
    dependentFirstName: optionalText(40),
    dependentLastName: optionalText(40),
    dependentDobMonth: z.coerce.number().int().min(1).max(12).optional(),
    dependentDobDay: z.coerce.number().int().min(1).max(31).optional(),
    dependentDobYear: z.coerce
      .number()
      .int()
      .min(thisYear - 120)
      .max(thisYear)
      .optional(),
    streetName: required('Street Name', 80),
    apartment: optionalText(40),
    zip: zip5,
    city: required('City', 40),
    mailingDifferent: z.boolean().default(false),
    mailingStreet: optionalText(80),
    mailingApartment: optionalText(40),
    mailingCity: optionalText(40),
    mailingState: optionalText(2),
    mailingZip: optionalText(5),
  })
  .superRefine((v, ctx) => {
    const issue = (path: string, message: string) =>
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: [path], message });
    if (!v.ssnLast4 && !v.tribalId) issue('ssnLast4', 'Enter SSN (Last 4 Digits) or Tribal ID');
    if (
      v.eligibilityProgram === FPHA_PROGRAM &&
      !(PUBLIC_HOUSING_TYPES as readonly string[]).includes(v.publicHousingType ?? '')
    )
      issue('publicHousingType', 'Select Public Housing Type');
    if (v.eligibilityProgram === SCHOOL_PROGRAM && !v.schoolName)
      issue('schoolName', 'School is required');
    const dob = dobOf(v.dobMonth, v.dobDay, v.dobYear);
    if (!dob) issue('dobDay', 'Enter a valid Date Of Birth');
    else if (new Date(`${dob}T00:00:00Z`) > new Date(Date.UTC(thisYear - 18, 11, 31)))
      // Assumption: the applicant is an adult; a child receives the benefit as a dependent.
      issue('dobYear', 'The applicant must be 18 or older');
    if (v.beneficiary === 'Child or Dependent in household') {
      if (!v.dependentFirstName) issue('dependentFirstName', 'Dependent First Name is required');
      if (!v.dependentLastName) issue('dependentLastName', 'Dependent Last Name is required');
      if (
        !v.dependentDobMonth ||
        !v.dependentDobDay ||
        !v.dependentDobYear ||
        !dobOf(v.dependentDobMonth, v.dependentDobDay, v.dependentDobYear)
      )
        issue('dependentDobDay', 'Enter a valid Dependent Date Of Birth');
    }
    if (v.mailingDifferent) {
      if (!v.mailingStreet) issue('mailingStreet', 'Mailing Street Name is required');
      if (!v.mailingCity) issue('mailingCity', 'Mailing City is required');
      if (!/^[A-Z]{2}$/.test(v.mailingState ?? '')) issue('mailingState', 'Select Mailing State');
      if (!/^\d{5}$/.test(v.mailingZip ?? '')) issue('mailingZip', 'Enter a 5-digit Mailing ZIP');
    }
  });
export type LifelinePersonalInput = z.input<typeof lifelinePersonalSchema>;
export type LifelinePersonalData = z.output<typeof lifelinePersonalSchema>;

export const lifelineConsentSchema = z
  .object({
    accepted: z.array(z.boolean()).length(LIFELINE_CONSENTS.length),
    signature: required('Signature', 80),
  })
  .superRefine((v, ctx) => {
    if (!v.accepted.every(Boolean))
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['accepted'],
        message: 'Tick every statement to continue',
      });
  });
export type LifelineConsentInput = z.input<typeof lifelineConsentSchema>;

export const lifelineAudioSchema = z
  .object({
    method: z.enum(AUDIO_AUTH_METHODS, { required_error: 'Select how it was authorized' }),
    reference: optionalText(60),
  })
  .superRefine((v, ctx) => {
    if (v.method === 'Recorded call' && !v.reference)
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['reference'],
        message: 'Enter the call recording reference',
      });
  });
export type LifelineAudioInput = z.input<typeof lifelineAudioSchema>;

/** Step 3: the Application ID the applicant got from the National Verifier. */
export const lifelineVerifySchema = z.object({
  applicationId: z.string().trim().max(40).optional().default(''),
});

export const lifelineEnrollSchema = z.object({ planId: z.string().min(1, 'Select a plan') });

export const lifelineRemovalSchema = z.object({
  reason: z.enum(LIFELINE_REMOVAL_REASONS, { required_error: 'Select a Reason' }),
});

export const lifelineListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(15),
  mode: z.enum(['all', 'completed', 'incomplete', 'rejected']).default('all'),
  q: optionalText(60),
  dateFrom: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  dateTo: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
});
export type LifelineListQuery = z.input<typeof lifelineListQuerySchema>;

export const lifelineStatusQuerySchema = z.object({ q: z.string().trim().min(1).max(40) });

/* ----------------------------------- DTOs --------------------------------- */

export interface LifelineDocumentDto {
  id: string;
  kind: string;
  fileName: string;
  contentType: string;
  size: number;
  uploadedAt: string;
}

export interface LifelineVerifierDto {
  applicationId: string;
  status: VerifierStatus;
  reason: string;
  checkedAt: string;
  /** True when the answer came from the simulation, not the National Verifier. */
  simulated: boolean;
}

export interface LifelineApplicationDto {
  id: string;
  enrollmentId: string;
  status: 'Incomplete' | 'Complete';
  /** The Lifeline step to do next; null once the plan is chosen (the order goes on in step 3). */
  stage: LifelineStage | null;
  /** The prepaid step the order is at once the plan is chosen (3 handover, 4 payment). */
  step: number | null;
  firstName: string;
  lastName: string;
  email: string;
  primaryPhone: string;
  dob: string;
  /** "***1234" for users with PC1164, otherwise "****"; empty when a Tribal ID was given. */
  ssnMasked: string;
  tribalId: string;
  eligibilityProgram: string;
  /** Only for Federal Public Housing Assistance. */
  publicHousingType: string;
  /** Only for School Lunch/Breakfast. */
  schoolName: string;
  beneficiary: string;
  dependent: { firstName: string; lastName: string; dob: string } | null;
  address: { street: string; apartment: string; city: string; state: string; zip: string };
  mailing: { street: string; apartment: string; city: string; state: string; zip: string } | null;
  consent: { signature: string; at: string } | null;
  documents: LifelineDocumentDto[];
  audio: { method: string; reference: string; at: string } | null;
  verifier: LifelineVerifierDto | null;
  plan: { id: string; name: string; priceCents: number } | null;
  customerId: number | null;
  /** The customer's record id, for links to the customer page. */
  customerRef: string | null;
  createdAt: string;
}

export interface LifelineListItemDto {
  id: string;
  enrollmentId: string;
  name: string;
  primaryPhone: string;
  state: string;
  zip: string;
  eligibilityProgram: string;
  verifierStatus: VerifierStatus | '';
  progress: string;
  planName: string;
  customerId: number | null;
  customerRef: string | null;
  createdAt: string;
}

export interface LifelinePlanOption {
  id: string;
  name: string;
  type: string;
  priceCents: number;
}

export interface LifelineRemovalResultDto {
  planName: string;
  priceCents: number;
}
